<template>
  <el-card class="translation-card" :class="{ 'dark-mode': isDarkMode }">
    <div class="language-selector">
      <el-select v-model="sourceLang" placeholder="Source Language">
        <el-option
          v-for="lang in languages"
          :key="lang.value"
          :label="lang.label"
          :value="lang.value">
        </el-option>
      </el-select>
      <el-button icon="el-icon-refresh" @click="swapLanguages"></el-button>
      <el-select v-model="targetLang" placeholder="Target Language">
        <el-option
          v-for="lang in languages"
          :key="lang.value"
          :label="lang.label"
          :value="lang.value">
        </el-option>
      </el-select>
    </div>

    <el-input
      type="textarea"
      :rows="4"
      placeholder="Enter text to translate"
      v-model="sourceText"
    >
    </el-input>

    <div class="translation-options">
      <el-radio-group v-model="translationSource">
        <el-radio label="deepl">DeepL</el-radio>
        <el-radio label="llm">Large Language Model</el-radio>
      </el-radio-group>
      <el-select v-if="translationSource === 'llm'" v-model="selectedModel" placeholder="Select Model">
        <el-option
          v-for="model in models"
          :key="model"
          :label="model"
          :value="model">
        </el-option>
      </el-select>
      <el-button type="primary" @click="translate">Translate</el-button>
    </div>

    <el-card v-if="translatedText" class="result-card">
      <div slot="header" class="result-header">
        <span>Translation Result</span>
        <div>
          <el-button icon="el-icon-document-copy" @click="copyText(translatedText)"></el-button>
          <el-button icon="el-icon-headset" @click="speakText(translatedText)"></el-button>
        </div>
      </div>
      <p>{{ translatedText }}</p>
    </el-card>

    <el-card v-if="alternatives.length > 0" class="result-card">
      <div slot="header" class="result-header">
        <span>Alternative Translations</span>
        <div>
          <el-button icon="el-icon-document-copy" @click="copyText(alternatives.join('\n'))"></el-button>
          <el-button icon="el-icon-headset" @click="speakText(alternatives.join('. '))"></el-button>
        </div>
      </div>
      <ul>
        <li v-for="(alt, index) in alternatives" :key="index">{{ alt }}</li>
      </ul>
    </el-card>
  </el-card>
</template>

<script>
export default {
  name: 'TranslationCard',
  data() {
    return {
      languages: [
        { value: 'EN', label: 'English' },
        { value: 'ZH-HANT', label: 'Chinese' },
        { value: 'JA', label: 'Japanese' },
        { value: 'KO', label: 'Korean' },
        { value: 'FR', label: 'French' },
        { value: 'DE', label: 'German' },
      ],
      sourceLang: 'EN',
      targetLang: 'ZH-HANT',
      sourceText: '',
      translatedText: '',
      alternatives: [],
      translationSource: 'deepl',
      models: [],
      selectedModel: '',
      isDarkMode: false,
    }
  },
  created() {
    this.loadSettings();
    this.isDarkMode = document.body.classList.contains('dark-mode');
  },
  mounted() {
    this.updateTheme();
  },
  methods: {
    updateTheme() {
      this.isDarkMode = document.body.classList.contains('dark-mode');
    },
    loadSettings() {
      this.models = (localStorage.getItem('VUE_APP_MODELS') || process.env.VUE_APP_MODELS || '').split(',').map(model => model.trim());
      this.selectedModel = this.models[0] || 'gpt-4o-mini';
    },
    getEnvVar(key) {
      return localStorage.getItem(key) || process.env[key];
    },
    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('Copy successful');
      }, () => {
        this.$message.error('Copy failed, please copy manually');
      });
    },
    swapLanguages() {
      [this.sourceLang, this.targetLang] = [this.targetLang, this.sourceLang];
    },
    async translate() {
      if (this.translationSource === 'deepl') {
        await this.translateWithDeepL();
      } else {
        await this.translateWithLLM();
      }
      this.$emit('translation-done', this.translatedText);
      this.saveToHistory();
    },
    async translateWithDeepL() {
      try {
        const response = await fetch(this.getEnvVar('VUE_APP_TRANSLATE_API_URL'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: this.sourceText,
            source_lang: this.sourceLang,
            target_lang: this.targetLang
          })
        });

        if (!response.ok) {
          throw new Error('Translation failed');
        }

        const data = await response.json();
        this.translatedText = data.data;
        this.alternatives = data.alternatives || [];
      } catch (error) {
        console.error('Translation error:', error);
        this.$message.error('Translation failed, please try again later');
      }
    },
    async translateWithLLM() {
      try {
        const response = await fetch(`${this.getEnvVar('VUE_APP_API_BASE_URL')}/v1/chat/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.getEnvVar('VUE_APP_API_KEY')}`
          },
          body: JSON.stringify({
            messages: [
              { role: 'system', content: 'You are a professional, authentic machine translation engine.' },
              { role: 'user', content: `Translate the following source text to ${this.getLanguageName(this.targetLang)}, Output translation directly without any additional text.\nSource Text: ${this.sourceText}\nTranslated Text:` }
            ],
            model: this.selectedModel,
            temperature: 0.7
          })
        });
    
        if (!response.ok) {
          throw new Error('Translation failed');
        }
    
        const data = await response.json();
        this.translatedText = data.choices[0].message.content;
        this.alternatives = [];
      } catch (error) {
        console.error('Translation error:', error);
        this.$message.error('Translation failed, please try again later');
      }
    },
    async speakText(text) {
      try {
        const ttsResponse = await fetch(`${this.getEnvVar('VUE_APP_TTS_API_URL')}/v1/audio/speech`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: this.getTTSModel(),
            input: text,
            voice: 'pitch:0.1|rate:0.2'
          })
        });

        if (!ttsResponse.ok) {
          throw new Error('TTS failed');
        }

        const audioBlob = await ttsResponse.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);
        audio.play();
      } catch (error) {
        console.error('TTS error:', error);
        this.$message.error('Speech playback failed, please try again later');
      }
    },
    getTTSModel() {
      const langModelMap = {
        'EN': 'en-US-JennyNeural',
        'ZH-HANT': 'zh-TW-HsiaoChenNeural',
        'JA': 'en-US-AvaMultilingualNeural',
        'KO': 'en-US-AvaMultilingualNeural',
        'FR': 'fr-FR-VivienneMultilingualNeural',
        'DE': 'de-DE-SeraphinaMultilingualNeural'
      };
      return langModelMap[this.targetLang] || 'en-US-JennyNeural';
    },
    saveToHistory() {
      const history = JSON.parse(localStorage.getItem('translationHistory') || '[]');
      history.unshift({
        sourceText: this.sourceText,
        translatedText: this.translatedText,
        sourceLang: this.sourceLang,
        targetLang: this.targetLang,
        timestamp: new Date().toISOString()
      });
      localStorage.setItem('translationHistory', JSON.stringify(history.slice(0, 10)));
    },
    getLanguageName(code) {
      const langs = {
        'EN': 'English',
        'ZH-HANT': 'Chinese',
        'JA': 'Japanese',
        'KO': 'Korean',
        'FR': 'French',
        'DE': 'German'
      };
      return langs[code] || code;
    }
  },
  props: {
    themeChanged: Number
  },
  watch: {
    themeChanged() {
      this.updateTheme();
    }
  }
}
</script>
<style scoped>

.dark-mode .el-radio__label {
  color: #fff;
}

.dark-mode .el-radio__input.is-checked .el-radio__inner {
  border-color: #409EFF;
  background: #409EFF;
}

.dark-mode .el-radio__input.is-checked+.el-radio__label {
  color: #409EFF;
}

.dark-mode .el-radio__inner {
  background-color: #000;
  border-color: #5a5a5a;
}

.dark-mode .el-radio__inner:hover {
  border-color: #409EFF;
}
.translation-card {
  margin-bottom: 20px;
}

.language-selector {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.translation-options {
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.result-card {
  margin-top: 20px;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

@media (max-width: 600px) {
  .language-selector {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .language-selector .el-button {
    padding: 8px;
    font-size: 14px;
  }

  .translation-options {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }
  
  .translation-options .el-radio-group {
    margin-bottom: 15px;
  }

  .translation-options .el-radio {
    margin-right: 15px;
  }
  
  .translation-options .el-select {
    margin-bottom: 15px;
  }

  .translation-options .el-button {
    width: 100%;
  }
}
</style>